import { ShellBridge, BridgeError, isUiAction, type UiAction, type ShopCartContext } from '@wianco/portal-bridge';
import { config } from '@/config/env';
import { useAuthStore } from '@/features/auth/store';
import { filterAuthorizedServices } from '@/features/auth/utils/authorization';

let bridge: ShellBridge | null = null;
let registration: object | null = null;
let active = false;
const listeners = new Set<() => void>();
const contextListeners = new Set<() => void>();
let cartContext: ShopCartContext | null = null;
export const getShopCartContext = () => cartContext;
export const isShopUiFrameRegistered = () => registration !== null;
export const subscribeShopCartContext = (listener: () => void) => { contextListeners.add(listener); return () => { contextListeners.delete(listener); }; };
const getBridge = () => bridge ??= new ShellBridge(window, 10_000, (context) => {
  if (JSON.stringify(context) === JSON.stringify(cartContext)) return;
  cartContext = context; contextListeners.forEach(listener => listener());
});
const announce = () => listeners.forEach(listener => listener());

/** Retry the initial hello: the iframe load event can precede Shop's React effect. */
export function ensureShopCartContext(timeout = 10_000): Promise<boolean> {
  if (cartContext?.ready) return Promise.resolve(true);
  return new Promise(resolve => {
    let finished = false;
    let handshaking = false;
    const finish = (ready: boolean) => {
      if (finished) return;
      finished = true; clearTimeout(timer);
      listeners.delete(check); contextListeners.delete(check); resolve(ready);
    };
    const check = () => {
      if (cartContext?.ready) { finish(true); return; }
      if (!registration || handshaking || finished) return;
      handshaking = true;
      void getBridge().waitUntilReady().then(() => {
        handshaking = false;
        if (cartContext?.ready) finish(true);
      }, () => { handshaking = false; });
    };
    const timer = setTimeout(() => finish(false), timeout);
    listeners.add(check); contextListeners.add(check); check();
  });
}

export function registerShopUiFrame(target: Window, origin: string, isActive: boolean) {
  const current = {};
  registration = current;
  active = isActive;
  getBridge().registerFrame(target, origin, isActive);
  announce();
  return () => {
    if (registration !== current) return;
    registration = null; active = false;
    bridge?.unregisterFrame(); announce();
  };
}
export function setShopUiFrameActive(value: boolean) { active = value; bridge?.setActive(value); announce(); }
export function resetShopUiSession() { bridge?.reset(); announce(); }

function identity() {
  const state = useAuthStore.getState();
  if (!state.isAuthenticated || !state.user?.sub) throw new BridgeError('SERVICE_NOT_ACTIVE');
  const permitted = filterAuthorizedServices(config().EMBEDDED_SERVICES, state.user).some(service => service.id === 'shop');
  if (!permitted) throw new BridgeError('SERVICE_NOT_ACTIVE');
  return JSON.stringify([state.user.sub, state.hubspotCustomerId, [...(state.user.permissions ?? [])].sort(), [...(state.user.roles ?? [])].sort()]);
}

/** Only presentation commands reach the frame. Cart writes always use its backend. */
export async function dispatchShopUiAction(action: UiAction, navigate: (path: string) => void) {
  if (!isUiAction(action)) throw new BridgeError('INVALID_PAYLOAD');
  const currentIdentity = identity();
  if (action.action !== 'shop.cart.refresh' && (!registration || !active)) {
    navigate('/shop');
    await new Promise<void>((resolve, reject) => {
      const ready = () => {
        if (!registration || !active) return;
        clearTimeout(timer); listeners.delete(ready); resolve();
      };
      const timer = setTimeout(() => { listeners.delete(ready); reject(new BridgeError('NOT_READY')); }, 10_000);
      listeners.add(ready); ready();
    });
  }
  if (!registration) throw new BridgeError('SERVICE_NOT_ACTIVE');
  if (identity() !== currentIdentity) throw new BridgeError('STALE_FRAME');
  await getBridge().dispatch(action);
  if (identity() !== currentIdentity) throw new BridgeError('STALE_FRAME');
}
